import React, { useEffect, useState } from 'react'
import Button from 'components/formElements/Button'
import AnimatedSpan from 'containers/animatedSpan/AnimatedSpan'
import { useRequest } from 'hooks/useRequest'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import * as FileSaver from 'file-saver'
import moment from 'moment/moment'
import useToast from '../hooks/useToast'
import { Calendar } from 'primereact/calendar'
import AccordionComp from '../components/accordion/Accordion'
import { currencyFormatter } from '../utils/CurrencyFormatter'
import { ColumnGroup } from 'primereact/columngroup'
import { Row } from 'primereact/row'

const OutsourceInvoiceReport = () => {
  const [getReport, loading] = useRequest()
  const { showToast } = useToast()
  const [invoiceReport, setInvoiceReport] = useState({})
  const [invoiceItemReport, setInvoiceItemReport] = useState([])
  const [requestBody, setRequestBody] = useState({
    invoiceDate: moment().toISOString(),
  })

  useEffect(() => {
    handleSubmit()
  }, [requestBody])

  const handleSubmit = () => {
    if (!requestBody.invoiceDate) {
      showToast('Lütfen fatura tarihi seçiniz', 'error')
      return
    }

    getReport('/Outsource/OutsourceInvoiceReport', { method: 'GET', params: requestBody }).then((res) => {
      if (res.data.ok) {
        setInvoiceReport(res.data.result)
      }
    })

    getReport('/Outsource/OutsourceInvoiceItemReport', { method: 'GET', params: requestBody }).then((res) => {
      if (res.data.ok) {
        setInvoiceItemReport(res.data.result)
      }
    })
  }

  const handleChange = (e) => {
    setRequestBody((prev) => ({ ...prev, [e.name]: e.value }))
  }

  const exportExcel = () => {
    import('xlsx').then((xlsx) => {
      const invoiceReportData = invoiceReport?.data.map((item) => {
        const rest = {
          'Şirket': item.projectName,
          'Fatura Kesilen Kişi Sayısı': item.itemsCount,
          'Toplam Fatura Tutarı': currencyFormatter(item.amount) + ' ' + item.currency,
          'Dönem': moment(item.invoiceDate).format('MMMM yy'),
        }
        return rest
      })
      invoiceReportData.push({})
      Object.keys(invoiceReport?.totalAmount).map((key) => {
        invoiceReportData.push({
          'Şirket': 'Toplam ' + key,
          'Fatura Kesilen Kişi Sayısı': currencyFormatter(invoiceReport?.totalAmount[key]) + ' ' + key
        })
      })
      invoiceReportData.push({})
      Object.keys(invoiceReport?.currencyRate).map((key) => {
        invoiceReportData.push({
          'Şirket': 'Kur ' + key,
          'Fatura Kesilen Kişi Sayısı': invoiceReport?.currencyRate[key].toLocaleString('tr-TR', { maximumFractionDigits: 4 })
        })
      })

      var invoiceItemReportData = invoiceItemReport.map((item) => {
        const rest = {
          'Personel Adı': item.name,
          'Şirket': item.projectName,
          'Günlük Ücret': item.isMonthlyPeriod ? '' : currencyFormatter(item.rate) + ' ' + item.currency,
          'Aylık Ücret': item.isMonthlyPeriod ? currencyFormatter(item.rate) + ' ' + item.currency : '',
          'Çalışma Günü': item.isMonthlyPeriod ? '' : item.days,
          'Toplam Fatura Tutarı': currencyFormatter(item.amount) + ' ' + item.currency,
          'Dönem': moment(item.invoiceDate).format('MMMM yy'),
        }
        return rest
      })
      const workSheet1 = xlsx.utils.json_to_sheet(invoiceReportData)
      const workSheet2 = xlsx.utils.json_to_sheet(invoiceItemReportData)
      const workBook = {
        Sheets: { 'Toplam Fatura Raporu': workSheet1, 'Personel Fatura Raporu': workSheet2 },
        SheetNames: ['Toplam Fatura Raporu', 'Personel Fatura Raporu'],
      }
      const excelBuffer = xlsx.write(workBook, {
        bookType: 'xlsx',
        type: 'array',
      })
      saveAsExcelFile(excelBuffer, 'Outsource Fatura Raporu - ' + moment(requestBody.invoiceDate).format('MMMM yy'))
    })
  }

  const saveAsExcelFile = (buffer, fileName) => {
    let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    let EXCEL_EXTENSION = '.xlsx'
    const data = new Blob([buffer], {
      type: EXCEL_TYPE,
    })
    FileSaver.saveAs(data, fileName + EXCEL_EXTENSION)
  }

  return (
    <AnimatedSpan className="page-container d-flex flex-column gap-4">
      <div className="card">
        <div className="d-flex justify-content-between">
          <div className="row flex-1">
            <div className="col-md-2 pl-0">
              <Calendar
                view="month"
                dateFormat="MM yy"
                placeholder="Dönem Seçiniz"
                value={requestBody.invoiceDate && moment(requestBody.invoiceDate).locale('en')._d}
                name="invoiceDate"
                onChange={(e) => {
                  handleChange({
                    name: e.target.name,
                    value: moment(e.target.value).add(3, 'hours').toISOString(),
                  })
                }}
              />
            </div>
            <div className="col-md-2">
              <Button
                disabled={loading}
                title="Raporu Al"
                handleClick={handleSubmit}
                className="btn-primary w-100 d-flex justify-content-center"
              />
            </div>
            <div className="col-md-2">
              <Button
                disabled={loading}
                title="Excel"
                handleClick={exportExcel}
                className="btn-success w-100 d-flex justify-content-center"
              />
            </div>
          </div>
        </div>
      </div>

      {!invoiceReport?.data?.length && !invoiceItemReport.length && !loading && (
        <div className="card">
          <div className="text-sm text-semi-bold">Gösterilecek Bir Veri Bulunmuyor</div>
        </div>
      )}

      {invoiceReport?.data?.length > 0 && (
        <div className="row-margin-x">
          <div className="col-md-12">
            <AccordionComp className="report-accordion" showHideTitle={false} onVal={false} title="Toplam Fatura Raporu">
              <DataTable
                responsiveLayout="scroll"
                className="p-1"
                value={invoiceReport?.data}
                loading={loading}
                footerColumnGroup={
                  <ColumnGroup>
                    {invoiceReport?.totalAmount && Object.keys(invoiceReport?.totalAmount).map((key) => {
                      return (
                        <Row key={key}>
                          <Column
                            footerClassName="text-bold text-default text-semi-bold text-sm"
                            footer={'Toplam ' + key}
                            colSpan={2}
                            style={{ textAlign: 'right' }}
                            footerStyle={{ textAlign: 'right' }} />
                          <Column
                            footerClassName="text-bold text-default text-semi-bold text-sm"
                            footer={currencyFormatter(invoiceReport?.totalAmount[key]) + ' ' + key}
                            colSpan={2} />
                        </Row>
                      )
                    })}
                    {invoiceReport?.currencyRate && Object.keys(invoiceReport?.currencyRate).map((key) => {
                      return (
                        <Row key={key}>
                          <Column
                            footerClassName="text-bold text-default text-semi-bold text-sm"
                            footer={'Kur ' + key}
                            colSpan={2}
                            style={{ textAlign: 'right' }}
                            footerStyle={{ textAlign: 'right' }} />
                          <Column
                            footerClassName="text-bold text-default text-semi-bold text-sm"
                            footer={invoiceReport?.currencyRate[key].toLocaleString('tr-TR', { maximumFractionDigits: 4 })}
                            colSpan={2} />
                        </Row>
                      )
                    })}
                  </ColumnGroup>
                }
              >
                <Column
                  sortable
                  className="text-semi-bold text-bold "
                  headerClassName="text-bold bg-primary text-default"
                  field="projectName"
                  style={{ width: 'auto' }}
                  header="Şirket"
                />
                <Column
                  className="text-semi-bold text-sm"
                  headerClassName="text-bold bg-primary text-default"
                  field="itemsCount"
                  header="Fatura Kesilen Kişi Sayısı"
                  style={{ width: 'auto' }}
                  body={(rowData) => {
                    return rowData.itemsCount
                  }}
                />
                <Column
                  className="text-semi-bold text-sm"
                  headerClassName="text-bold bg-primary text-default"
                  field="amount"
                  header="Toplam Fatura Tutarı"
                  sortable="true"
                  style={{ width: 'auto' }}
                  body={(rowData) => {
                    return (
                      <span>{currencyFormatter(rowData.amount)} {rowData.currency}</span>
                    )
                  }}
                />
                <Column
                  sortable
                  className="text-semi-bold text-sm"
                  headerClassName="text-bold bg-primary text-default"
                  field="invoiceDate"
                  header="Dönem"
                  body={(rowData) => {
                    return rowData.invoiceDate && moment(rowData.invoiceDate).format('MMMM yy')
                  }}
                />
              </DataTable>
            </AccordionComp>
          </div>
        </div>
      )}

      {invoiceItemReport.length > 0 && (
        <div className="row-margin-x">
          <div className="col-md-12">
            <AccordionComp className="report-accordion" showHideTitle={false} onVal={false} title="Personel Fatura Raporu">
              <DataTable
                stripedRows
                paginator
                responsiveLayout="scroll"
                paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                rows={25}
                className="p-1"
                paginatorLeft={<Button type="button" className="p-button-text" />}
                paginatorRight={<Button type="button" className="p-button-text" />}
                value={invoiceItemReport}
                dataKey="id"
                loading={loading}
              >
                <Column
                  sortable
                  className="text-semi-bold text-bold "
                  headerClassName="text-bold bg-primary text-default"
                  field="name"
                  style={{ width: 'auto' }}
                  header="Personel Adı"
                />
                <Column
                  sortable
                  className="text-semi-bold text-bold "
                  headerClassName="text-bold bg-primary text-default"
                  field="projectName"
                  style={{ width: 'auto' }}
                  header="Şirket"
                />
                <Column
                  className="text-semi-bold text-sm"
                  headerClassName="text-bold bg-primary text-default"
                  field="rate"
                  header="Günlük Ücret"
                  sortable="true"
                  style={{ width: 'auto' }}
                  body={(rowData) => {
                    return (
                      !rowData.isMonthlyPeriod ? <span>{currencyFormatter(rowData.rate)} {rowData.currency}</span> : ''
                    )
                  }}
                />
                <Column
                  className="text-semi-bold text-sm"
                  headerClassName="text-bold bg-primary text-default"
                  field="rate"
                  header="Aylık Ücret"
                  sortable="true"
                  style={{ width: 'auto' }}
                  body={(rowData) => {
                    return (
                      rowData.isMonthlyPeriod ? <span>{currencyFormatter(rowData.rate)} {rowData.currency}</span> : ''
                    )
                  }}
                />
                <Column
                  className="text-semi-bold text-sm"
                  headerClassName="text-bold bg-primary text-default"
                  field="days"
                  header="Çalışma Günü"
                  sortable="true"
                  style={{ width: '10rem' }}
                  body={(rowData) => {
                    return <span>{rowData.isMonthlyPeriod ? '' : rowData.days}</span>
                  }}
                />
                <Column
                  className="text-semi-bold text-sm"
                  headerClassName="text-bold bg-primary text-default"
                  field="amount"
                  header="Toplam Fatura Tutarı"
                  sortable="true"
                  style={{ width: 'auto' }}
                  body={(rowData) => {
                    return (
                      <span>{currencyFormatter(rowData.amount)} {rowData.currency}</span>
                    )
                  }}
                />
                <Column
                  sortable
                  className="text-semi-bold text-sm"
                  headerClassName="text-bold bg-primary text-default"
                  field="invoiceDate"
                  header="Dönem"
                  body={(rowData) => {
                    return rowData.invoiceDate && moment(rowData.invoiceDate).format('MMMM yy')
                  }}
                />
              </DataTable>
            </AccordionComp>
          </div>
        </div>
      )}
    </AnimatedSpan>
  )
}

export default OutsourceInvoiceReport
